import { createApi } from '@reduxjs/toolkit/query/react';
import { prepareBaseQuery } from '../shared/configs/config';

export type VendorPaymentStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'voided'
  | 'filed'
  | 'paid'
  | 'partially_paid';

export interface VendorPaymentPayable {
  id?: string;
  type?: string;
  reference?: string;
  date?: string;
  description?: string;
  amount?: number | string;
  propertyId?: string;
  propertyAbbreviation?: string;
}

export interface VendorPaymentSourceMetadata {
  paymentId?: string;
  amount?: number | string;
  method?: string;
  vendorName?: string;
  vendorAbbreviation?: string;
  payables?: VendorPaymentPayable[];
  _sourceBankAccountNumber?: string;
  [key: string]: unknown;
}

export interface VendorPayment {
  id: string;
  customer_id: string;
  property_id: string;
  vendor_account_id: string;
  amount: number | string;
  method: string;
  source_system: 'resman' | 'coupa' | 'manual';
  source_payment_id: string;
  status: VendorPaymentStatus;
  matched_rule_id: string | null;
  assigned_approver_user_ids: string[];
  approved_by_user_id: string | null;
  approved_at: string | null;
  rejected_by_user_id: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  cancelled_by_user_id: string | null;
  cancelled_at: string | null;
  voided_at: string | null;
  filed_at: string | null;
  ach_file_id: string | null;
  created_by_user: string | null;
  source_metadata: VendorPaymentSourceMetadata | null;
  created_at: string;
  updated_at: string;
  matchedRule?: {
    id: string;
    name: string;
    approver_role_label: string;
  } | null;
  approvedByUser?: VendorPaymentActor | null;
  rejectedByUser?: VendorPaymentActor | null;
  cancelledByUser?: VendorPaymentActor | null;
  assignedApprovers?: VendorPaymentActor[];
  property?: {
    id: string;
    name: string;
    code?: string | null;
  } | null;
  stripeMode?: 'test' | 'live';
}

export interface VendorPaymentActor {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface ListVendorPaymentsArgs {
  customer_id?: string;
  status?: string;
  property_id?: string;
  vendor_account_id?: string;
  method?: string;
  assigned_approver_user_id?: string;
  limit?: number;
  offset?: number;
}

export interface BulkOutcome {
  id: string;
  ok: boolean;
  status?: VendorPaymentStatus;
  error?: string;
}

export interface BulkMutationResponse {
  ok: BulkOutcome[];
  failed: BulkOutcome[];
}

export const vendorPaymentsApi = createApi({
  reducerPath: 'vendorPaymentsApi',
  tagTypes: ['VendorPayments'],
  baseQuery: prepareBaseQuery(),
  endpoints: (builder) => ({
    listVendorPayments: builder.query<VendorPayment[], ListVendorPaymentsArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(args)) {
          if (v !== undefined && v !== null && v !== '') {
            params.set(k, String(v));
          }
        }
        const qs = params.toString();
        return {
          url: `/vendor-payment${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['VendorPayments'],
    }),
    getVendorPayment: builder.query<VendorPayment, string>({
      query: (id) => ({
        url: `/vendor-payment/${id}`,
        method: 'GET',
      }),
      providesTags: ['VendorPayments'],
    }),
    getMyPendingApprovalCount: builder.query<{ count: number }, void>({
      query: () => ({
        url: `/vendor-payment/my-pending-count`,
        method: 'GET',
      }),
      providesTags: ['VendorPayments'],
    }),
    getApprovalSchedule: builder.query<
      { syncDescription: string; filingDescription: string },
      void
    >({
      query: () => ({
        url: `/vendor-payment/schedule`,
        method: 'GET',
      }),
    }),
    approveVendorPayments: builder.mutation<BulkMutationResponse, string[]>({
      query: (ids) => ({
        url: `/vendor-payment/approve`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['VendorPayments'],
    }),
    rejectVendorPayments: builder.mutation<
      BulkMutationResponse,
      { ids: string[]; rejection_reason: string }
    >({
      query: (body) => ({
        url: `/vendor-payment/reject`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VendorPayments'],
    }),
    cancelVendorPayments: builder.mutation<BulkMutationResponse, string[]>({
      query: (ids) => ({
        url: `/vendor-payment/cancel`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['VendorPayments'],
    }),
  }),
});

export const {
  useListVendorPaymentsQuery,
  useGetVendorPaymentQuery,
  useGetMyPendingApprovalCountQuery,
  useGetApprovalScheduleQuery,
  useApproveVendorPaymentsMutation,
  useRejectVendorPaymentsMutation,
  useCancelVendorPaymentsMutation,
} = vendorPaymentsApi;
